import { createContext, useContext } from 'react';

interface DashboardEditLockContextValue {
  /**
   * Requests an edit session and claims the tab as the edit lock holder.
   * If another browser tab already holds the edit lock, a conflict dialog is shown
   * allowing the user to either take over or cancel.
   * If no other tab holds the lock, `onProceed` is called immediately.
   */
  requestEditLock: (onProceed: () => void) => void;
  /**
   * Guards an exclusive action (adding an app, creating from symbol selector, etc.).
   * This does not claim the edit lock; the holder tab is tracked only while a
   * Dashboard is in Edit mode.
   */
  requestExclusiveAction: (onProceed: () => void) => void;
  /** Release the lock — call when exiting Edit mode. */
  releaseEditLock: () => void;
  /**
   * Register a callback that fires when another tab sends FORCE_RELEASE.
   * The Dashboard uses this to exit Edit mode when another tab takes over.
   */
  setForceReleaseCallback: (cb: () => void) => void;
}

export const DashboardEditLockContext = createContext<DashboardEditLockContextValue | null>(null);

export const useDashboardEditLockContext = () => {
  const ctx = useContext(DashboardEditLockContext);
  if (!ctx)
    throw new Error('useDashboardEditLockContext must be used within DashboardEditLockProvider');
  return ctx;
};
